import {apiService} from '../apiService';
import {sharedTemplateFromApi} from './templateFromApi';

const getApiErrorMessage = (error, fallback) => {
    const validationErrors = error?.data?.validationErrors;

    if (validationErrors?.length > 0) {
        return validationErrors.map(x => x.errorMessage).join(' ');
    }

    if (typeof error?.data === 'string' && error.data.length > 0) {
        return error.data;
    }

    return error?.data?.message ?? error?.data?.title ?? fallback;
};

export const formTemplateLibraryService = apiService.injectEndpoints({
    endpoints: builder => ({
        getSharedFormTemplates: builder.query({
            query: (hidden) => ({
                url: `/forms/templates/shared?hidden=${hidden ? 'true' : 'false'}`
            }),
            transformErrorResponse: (_) => ({message: 'Failed to retrieve the form templates'}),
            transformResponse: (response) => response.map(sharedTemplateFromApi),
            providesTags: [{type: 'SharedFormTemplates', id: 'LIST'}]
        }),
        createSharedFormTemplate: builder.mutation({
            query: (body) => ({
                url: '/forms/templates/shared',
                method: 'POST',
                data: body
            }),
            transformErrorResponse: (error) => ({
                message: getApiErrorMessage(error, 'Failed to create the form template')
            }),
            invalidatesTags: [{type: 'SharedFormTemplates', id: 'LIST'}]
        }),
        updateSharedFormTemplate: builder.mutation({
            query: ({templateId, ...body}) => ({
                url: `/forms/templates/shared/${templateId}`,
                method: 'PUT',
                data: body
            }),
            transformErrorResponse: (error) => ({
                message: getApiErrorMessage(error, 'Failed to save the form template')
            }),
            invalidatesTags: [{type: 'SharedFormTemplates', id: 'LIST'}]
        }),
        setSharedFormTemplateHidden: builder.mutation({
            query: ({templateId, hidden}) => ({
                url: `/forms/templates/shared/${templateId}/hidden`,
                method: 'PUT',
                data: {hidden}
            }),
            transformErrorResponse: (error) => ({
                message: getApiErrorMessage(error, 'Failed to change the form template')
            }),
            invalidatesTags: [{type: 'SharedFormTemplates', id: 'LIST'}]
        }),
        changeSharedFormTemplateCategory: builder.mutation({
            query: (body) => ({
                url: '/forms/templates/shared/category',
                method: 'PUT',
                data: body
            }),
            transformErrorResponse: (error) => ({
                message: getApiErrorMessage(error, 'Failed to change the category')
            }),
            invalidatesTags: [{type: 'SharedFormTemplates', id: 'LIST'}]
        }),
        getFormTemplateCategories: builder.query({
            query: () => ({
                url: '/forms/template-categories'
            }),
            transformErrorResponse: (_) => ({message: 'Failed to retrieve the categories'}),
            providesTags: [{type: 'FormTemplateCategories', id: 'LIST'}]
        }),
        createFormTemplateCategory: builder.mutation({
            query: (body) => ({
                url: '/forms/template-categories',
                method: 'POST',
                data: body
            }),
            transformErrorResponse: (error) => ({
                message: getApiErrorMessage(error, 'Failed to create the category')
            }),
            invalidatesTags: [{type: 'FormTemplateCategories', id: 'LIST'}]
        }),
        renameFormTemplateCategory: builder.mutation({
            query: ({categoryId, ...body}) => ({
                url: `/forms/template-categories/${categoryId}`,
                method: 'PUT',
                data: body
            }),
            transformErrorResponse: (error) => ({
                message: getApiErrorMessage(error, 'Failed to rename the category')
            }),
            invalidatesTags: [
                {type: 'FormTemplateCategories', id: 'LIST'},
                {type: 'SharedFormTemplates', id: 'LIST'}
            ]
        }),
        deleteFormTemplateCategory: builder.mutation({
            query: (categoryId) => ({
                url: `/forms/template-categories/${categoryId}`,
                method: 'DELETE'
            }),
            transformErrorResponse: (error) => ({
                message: getApiErrorMessage(error, 'Failed to delete the category')
            }),
            invalidatesTags: [{type: 'FormTemplateCategories', id: 'LIST'}]
        })
    })
});

export const {
    useGetSharedFormTemplatesQuery,
    useCreateSharedFormTemplateMutation,
    useUpdateSharedFormTemplateMutation,
    useSetSharedFormTemplateHiddenMutation,
    useChangeSharedFormTemplateCategoryMutation,
    useGetFormTemplateCategoriesQuery,
    useCreateFormTemplateCategoryMutation,
    useRenameFormTemplateCategoryMutation,
    useDeleteFormTemplateCategoryMutation
} = formTemplateLibraryService;
